import { WidgetProps } from "@rjsf/core";
import { useContactTypes } from "components/utils/useContactTypes";
import { useCallback, useEffect, useState } from "react";
import { ContractorContactForm } from "./ContractorContactForm";
import { PremiseContactForm } from "./PremiseContactForm";
import { PrimaryContactForm } from "./PrimaryContactForm";
import { ContactFormData, ContactInboundData, ContactValidationResult, ContractorFormData } from "./types";
import { ContactType } from "components/utils/contacts";
import { useApplicationContactRequirements } from "./useApplicationContactRequirements";
import { refreshApplicationContacts, useApplicationContacts } from "./useApplicationContacts";
import {
    createContactFormDataObject,
    createContractorFormDataObjectFromContact,
    getContactTypeByName,
    getCopyContactState,
    validate,
    validateContactData,
    validateDataFormat,
    getStoredContactState,
} from "./utils";
import { useCustomerContacts } from "components/utils/useCustomerContacts";
import { useContractorLabel } from "./useContractorLabel";
import { isEmpty } from "lodash";

const ApplicationContactsWidget = (props: WidgetProps) => {
    const { applicationNumber, contactsWidgetRef, onContactsLoaded } = props.formContext ?? {};
    const [requirements, isLoadingRequirements] = useApplicationContactRequirements(applicationNumber);
    const [contacts, isLoadingContacts] = useApplicationContacts(applicationNumber);
    const [contactTypes = [], isLoadingContactTypes] = useContactTypes();
    const [customerContacts = [], isLoadingCustomerContacts] = useCustomerContacts(1, 100);

    const contractorLabel = useContractorLabel();

    const [primaryContactData, setPrimaryContactData] = useState<ContactFormData>({});
    const [premiseContactData, setPremiseContactData] = useState<ContactFormData>({});
    const [contractorContactData, setContractorContactData] = useState<ContractorFormData>({});

    const [primaryContactErrors, setPrimaryContactErrors] = useState<ContactValidationResult>();
    const [premiseContactErrors, setPremiseContactErrors] = useState<ContactValidationResult>();
    const [contractorContactErrors, setContractorContactErrors] = useState<ContactValidationResult>();

    const isLoading = isLoadingRequirements || isLoadingContactTypes || isLoadingContacts || isLoadingCustomerContacts;

    // Setup widget
    useEffect(() => {
        if (contactsWidgetRef) {
            contactsWidgetRef.current = {
                validate: () => {
                    const errors = validate({
                        primaryContactData,
                        premiseContactData,
                        contractorContactData,
                        requirements,
                        contractorLabel,
                    });
                    setPrimaryContactErrors(errors.primaryContactErrors);
                    setPremiseContactErrors(errors.premiseContactErrors);
                    setContractorContactErrors(errors.contractorContactErrors);

                    return errors.primaryContactErrors || errors.premiseContactErrors || errors.contractorContactErrors;
                },
                validateDataFormat: () => {
                    const errors = validateDataFormat({
                        primaryContactData,
                        premiseContactData,
                        contractorContactData,
                        requirements,
                        contractorLabel,
                    });
                    setPrimaryContactErrors(errors.primaryContactErrors);
                    setPremiseContactErrors(errors.premiseContactErrors);
                    setContractorContactErrors(errors.contractorContactErrors);

                    return errors.primaryContactErrors || errors.premiseContactErrors || errors.contractorContactErrors;
                },
                getSubmitValues: () => {
                    let values = [];

                    if (requirements.requirePremise) {
                        const contact = contacts?.find((c) => c.contactType.toLowerCase() === ContactType.Premise);
                        const contactType = getContactTypeByName(contactTypes, ContactType.Premise);

                        // Add contact if form has at least some data filled
                        if (!(Object.values(premiseContactData ?? {}).every(isEmpty) && !contact?.contactNumber)) {
                            values.push({
                                ...premiseContactData,
                                contactNumber: contact?.contactNumber,
                                workflowTargetGroupId: contact?.workflowTargetGroupId,
                                applicationContactType: ContactType.Premise,
                                contactType,
                                contactTitle: premiseContactData.contactTitle || "Premise contact",
                            });
                        }
                    }

                    if (requirements.requirePrimaryContact) {
                        const contact = contacts?.find((c) => c.contactType.toLowerCase() === ContactType.Primary);
                        const contactType = getContactTypeByName(contactTypes, ContactType.Primary);

                        // Add contact if form has at least some data filled
                        if (!(Object.values(primaryContactData ?? {}).every(isEmpty) && !contact?.contactNumber)) {
                            values.push({
                                ...primaryContactData,
                                contactNumber: contact?.contactNumber,
                                workflowTargetGroupId: contact?.workflowTargetGroupId,
                                applicationContactType: ContactType.Primary,
                                contactType,
                                contactTitle: premiseContactData.contactTitle || "Primary contact",
                            });
                        }
                    }

                    if (requirements.requireContractor) {
                        const contact = contacts?.find((c) => c.contactType.toLowerCase() === ContactType.Contractor);
                        const contactType = getContactTypeByName(contactTypes, ContactType.Contractor) as number;

                        // Add contact if form has at least some data filled
                        if (!(Object.values(contractorContactData ?? {}).every(isEmpty) && !contact?.contactNumber)) {
                            values.push({
                                ...contractorContactData,
                                contactNumber: contact?.contactNumber,
                                workflowTargetGroupId: contact?.workflowTargetGroupId,
                                applicationContactType: ContactType.Contractor,
                                contactType,
                                contactTitle: premiseContactData.contactTitle || "Contractor contact",
                            });
                        }
                    }

                    return values;
                },
                fieldNumber: props.uiSchema["af:fieldNumber"],
            };
        }

        return () => {
            if (contactsWidgetRef?.current) {
                contactsWidgetRef.current = undefined;
            }
        };
    }, [
        contactTypes,
        contacts,
        contactsWidgetRef,
        props.uiSchema,
        requirements,
        contractorLabel,
        premiseContactData,
        primaryContactData,
        contractorContactData,
    ]);
    // Init contact data
    useEffect(() => {
        if (contacts) {
            const primaryContact = contacts.find((c) => c.contactType.toLowerCase() === ContactType.Primary);
            const primaryContactData = createContactFormDataObject(primaryContact);

            const premiseContact = contacts.find((c) => c.contactType.toLowerCase() === ContactType.Premise);
            const premiseContactData = createContactFormDataObject(premiseContact);

            if (primaryContactData) {
                if (premiseContactData && getCopyContactState(premiseContactData, primaryContactData)) {
                    primaryContactData.copyContact = true;
                }
                setPrimaryContactData(primaryContactData);
            }

            if (premiseContactData) {
                setPremiseContactData(premiseContactData);
            }

            const contractorContact = contacts.find((c) => c.contactType.toLowerCase() === ContactType.Contractor);
            const contractorContactData = createContractorFormDataObjectFromContact(contractorContact);
            if (contractorContactData) {
                setContractorContactData(contractorContactData);
            }
        }
    }, [contacts]);

    useEffect(() => {
        if (!isLoading) {
            onContactsLoaded?.();
        }
    }, [isLoading, onContactsLoaded]);

    // Clear contact data on unmount
    useEffect(() => {
        return () => {
            refreshApplicationContacts(applicationNumber);
        };
    }, [applicationNumber]);

    const onUseCustomerContactForPremiseContact = useCallback(
        (selectedContact: ContactInboundData | undefined) => {
            if (!selectedContact) return;
            const formData = createContactFormDataObject(selectedContact);
            if (!formData) return;

            setPremiseContactData({ ...formData, storedContactNumber: selectedContact.contactNumber });
            setPremiseContactErrors(
                validateContactData({
                    contact: formData,
                    contactType: ContactType.Premise,
                    requirements,
                    isCustomerContact: false,
                })
            );
        },
        [requirements]
    );

    const onUseCustomerContactPrimaryContact = useCallback(
        (selectedContact: ContactInboundData | undefined) => {
            if (!selectedContact) return;
            const formData = createContactFormDataObject(selectedContact);
            if (!formData) return;

            setPrimaryContactData({ ...formData, storedContactNumber: selectedContact.contactNumber });
            setPrimaryContactErrors(
                validateContactData({
                    contact: formData,
                    contactType: ContactType.Primary,
                    requirements,
                    isCustomerContact: false,
                })
            );
        },
        [requirements]
    );

    const onUseCustomerContactContractor = useCallback(
        (selectedContact: ContactInboundData | undefined) => {
            if (!selectedContact) return;
            const formData = createContractorFormDataObjectFromContact(selectedContact);
            if (!formData) return;

            setContractorContactData({ ...formData, storedContactNumber: selectedContact.contactNumber });
            setContractorContactErrors(
                validateContactData({
                    contact: formData,
                    contactType: ContactType.Contractor,
                    requirements,
                    isCustomerContact: false,
                })
            );
        },
        [requirements]
    );

    const onCopyPremiseToPrimaryContact = useCallback(() => {
        setPrimaryContactData({
            firstName: premiseContactData.firstName,
            lastName: premiseContactData.lastName,
            company: premiseContactData.company,
            premiseId: premiseContactData.premiseId,
            meterId: premiseContactData.meterId,
            acct_number: premiseContactData.acct_number,
            lot: premiseContactData.lot,
            address: premiseContactData.address,
            address_cont: premiseContactData.address_cont,
            city: premiseContactData.city,
            state: premiseContactData.state,
            zip: premiseContactData.zip,
            phone: premiseContactData.phone,
            cell: premiseContactData.cell,
            fax: premiseContactData.fax,
            email: premiseContactData.email,
            taxId: premiseContactData.taxId,
            copyContact: true,
            storedContactNumber: premiseContactData.storedContactNumber,
        });
    }, [premiseContactData]);

    const onChange = useCallback(
        (formData: any, contactType: ContactType) => {
            if (contactType === "premise") {
                const storedContactNumber = getStoredContactState(formData, customerContacts);
                if (storedContactNumber === undefined || formData.storedContactNumber !== storedContactNumber) {
                    setPremiseContactData({
                        ...formData,
                        storedContactNumber,
                    });
                }
            }
            if (contactType === "primary") {
                const storedContactNumber = getStoredContactState(formData, customerContacts);
                let copyContact = Boolean(formData.copyContact);
                if (!isEmpty(premiseContactData)) {
                    copyContact = getCopyContactState(premiseContactData, formData);
                }

                if (
                    storedContactNumber === undefined ||
                    Boolean(formData.copyContact) !== copyContact ||
                    formData.storedContactNumber !== storedContactNumber
                ) {
                    setPrimaryContactData({
                        ...formData,
                        copyContact,
                        storedContactNumber,
                    });
                }
            }

            if (contactType === "contractor") {
                const storedContactNumber = getStoredContactState(formData, customerContacts, true);
                if (storedContactNumber === undefined || formData.storedContactNumber !== storedContactNumber) {
                    setContractorContactData({
                        ...formData,
                        storedContactNumber,
                    });
                }
            }
        },
        [customerContacts, premiseContactData]
    );

    const onClearForm = useCallback((contactType: ContactType) => {
        if (contactType === ContactType.Premise) {
            setPremiseContactData({});
        }

        if (contactType === ContactType.Primary) {
            setPrimaryContactData({});
        }

        if (contactType === ContactType.Contractor) {
            setContractorContactData({});
        }
    }, []);

    if (isLoading || props.readonly) {
        return null;
    }

    return (
        <div className="application-contacts-widget">
            <PremiseContactForm
                formData={premiseContactData}
                errors={premiseContactErrors}
                requirements={requirements}
                onUseCustomerContact={onUseCustomerContactForPremiseContact}
                onChange={(form) => onChange(form, ContactType.Premise)}
                onClearForm={() => onClearForm(ContactType.Premise)}
            />
            <PrimaryContactForm
                formData={primaryContactData}
                errors={primaryContactErrors}
                requirements={requirements}
                onCopyContact={requirements.requirePremise ? onCopyPremiseToPrimaryContact : undefined}
                onUseCustomerContact={onUseCustomerContactPrimaryContact}
                onChange={(form) => onChange(form, ContactType.Primary)}
                onClearForm={() => onClearForm(ContactType.Primary)}
            />
            <ContractorContactForm
                formData={contractorContactData}
                errors={contractorContactErrors}
                requirements={requirements}
                applicationNumber={applicationNumber}
                onUseCustomerContact={onUseCustomerContactContractor}
                onChange={(form) => onChange(form, ContactType.Contractor)}
                onClearForm={() => onClearForm(ContactType.Contractor)}
            />
        </div>
    );
};

export default ApplicationContactsWidget;
